import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { Observable, tap } from 'rxjs';

import { EntityTypeConstants } from 'src/app/common/EntityTypeConstants';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { LANGUAGE_SETTING, LanguagePreferenceService } from '../../services/language-preference.service';
import {
  DEFAULT_LIST_PAGE_SIZE,
  LIST_PAGE_SIZE_SETTING,
  UserSettingsService,
} from '../../services/user-settings.service';
import { languageOptions, SettingsPageStore } from './settings-page.store';

function buildUserSettingsForm(fb: FormBuilder, bundleLanguage: string) {
  return fb.group({
    listPageSize: [DEFAULT_LIST_PAGE_SIZE],
    language: [bundleLanguage],
  });
}

/**
 * #648 / #178 slice B: the user's own page size and reading language (Benutzer tab). Both rows
 * are Profile-scoped, so they travel in one batch — that is what batchcreateorupdate is for, and
 * it keeps the two from half-saving.
 */
@Component({
  selector: 'app-user-settings',
  imports: [ReactiveFormsModule, AsyncClickDirective, KENDO_DROPDOWNS],
  templateUrl: './user-settings.component.html',
})
export class UserSettingsComponent {
  private readonly store = inject(SettingsPageStore);
  private readonly userSettingsService = inject(UserSettingsService);
  private readonly languagePreference = inject(LanguagePreferenceService);

  readonly profile = this.store.profile;
  readonly pageSizes = [10, 25, 50, 100, 200, 500];
  readonly languages = languageOptions();
  // The reading language starts on the bundle actually on screen, so an unsaved form never
  // claims a language the user is not looking at.
  readonly form = buildUserSettingsForm(inject(FormBuilder), this.languagePreference.bundleLanguage);

  constructor() {
    this.store.patchOnRead(EntityTypeConstants.EntityTypeProfile, this.form, () => {
      // Profiles created before the reseed have no ListPageSize row yet — the default it is.
      const pageSize = Number(this.store.value(EntityTypeConstants.EntityTypeProfile, LIST_PAGE_SIZE_SETTING));
      this.form.patchValue({
        listPageSize: Number.isInteger(pageSize) && pageSize > 0 ? pageSize : DEFAULT_LIST_PAGE_SIZE,
        language: this.storedLanguage() ?? this.languagePreference.bundleLanguage,
      });
    });
  }

  /**
   * The reading language stored, or null when there is none this build can serve — no row at
   * all, on a profile that has not signed in since the setting existed, shows as the bundle on
   * screen, which is also what the start-up reconciliation would adopt.
   */
  private storedLanguage(): string | null {
    const stored = this.store.value(EntityTypeConstants.EntityTypeProfile, LANGUAGE_SETTING);
    return this.languagePreference.isSupported(stored) ? stored : null;
  }

  save(): Observable<unknown> {
    const scope = EntityTypeConstants.EntityTypeProfile;
    const { listPageSize, language } = this.form.getRawValue();
    const batch = [this.store.row(scope, LIST_PAGE_SIZE_SETTING, String(listPageSize))];
    const languageChanged = this.languagePreference.isSupported(language) && language !== this.storedLanguage();
    if (this.languagePreference.isSupported(language)) {
      batch.push(this.store.row(scope, LANGUAGE_SETTING, language));
    }

    return this.store.save$(batch).pipe(
      tap(() => {
        this.form.markAsPristine();

        // The reading language is the one setting on this page whose effect is the page itself.
        // Leaving the user on the old bundle would put the form and what they are looking at in
        // disagreement, so a changed language switches, and the reload does the re-read. Only
        // when it really navigated, though: saving the language the bundle already renders
        // changes the stored row without going anywhere, and that still has to be re-read.
        if (languageChanged && this.languagePreference.redirectTo(language)) {
          return;
        }

        this.store.reload(scope);
        // Every list view sizes its pages from this cache.
        this.userSettingsService.refresh();
      }),
    );
  }
}
